import React, { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { RolePicker, ClientPicker } from "./AddMemberDialog";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export const MemberActions = ({ member, subAccounts, onChanged }) => {
  const [dialog, setDialog] = useState(null);
  const [role, setRole] = useState(member.role);
  const [subs, setSubs] = useState(member.sub_account_ids || []);
  const [pw, setPw] = useState("");
  const id = member.id;

  const run = async (fn, msg) => { try { await fn(); toast.success(msg); setDialog(null); setPw(""); onChanged(); } catch (e) { err(e); } };
  const saveEdit = () => run(() => api.put(`/users/${id}`, { role, sub_account_ids: subs }), "Member updated");
  const savePw = () => run(() => api.post(`/users/${id}/reset-password`, { password: pw }), "Password reset — they'll choose a new one on login");
  const remove = () => run(() => api.delete(`/users/${id}`), "Member removed");
  const copyInvite = async () => {
    try {
      const { data } = await api.post(`/users/${id}/invite-link`);
      navigator.clipboard.writeText(data.invite_link);
      toast.success(data.email_sent ? "New invite emailed and link copied" : "Invite link copied");
    } catch (e) { err(e); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" data-testid={`member-actions-${id}`}><MoreHorizontal className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setDialog("edit")} data-testid={`member-edit-${id}`}>Change access</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog("pw")} data-testid={`member-reset-${id}`}>Reset password</DropdownMenuItem>
          {member.pending_invite && <DropdownMenuItem onClick={copyInvite} data-testid={`member-invite-${id}`}>Resend invite & copy link</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => setDialog("del")} className="text-destructive" data-testid={`member-remove-${id}`}>Remove</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle>{{ edit: "Change access", pw: "Reset password", del: "Remove member" }[dialog]} — {member.name}</DialogTitle></DialogHeader>
          {dialog === "edit" && <div className="space-y-4 py-2">
            <RolePicker value={role} onChange={setRole} />
            {role !== "agency_admin" && <ClientPicker subAccounts={subAccounts} value={subs} onChange={setSubs} />}
          </div>}
          {dialog === "pw" && <div className="space-y-1.5 py-2"><Label>New temporary password (min 8)</Label>
            <Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
            <p className="text-xs text-muted-foreground">They'll be signed out everywhere.</p></div>}
          {dialog === "del" && <p className="text-sm text-muted-foreground py-2">{member.name} will be signed out and won't be able to log in again.</p>}
          <DialogFooter>
            {dialog === "edit" && <Button onClick={saveEdit} disabled={role !== "agency_admin" && !subs.length} data-testid="member-save-button">Save</Button>}
            {dialog === "pw" && <Button onClick={savePw} disabled={pw.length < 8} data-testid="member-reset-save-button">Reset password</Button>}
            {dialog === "del" && <Button variant="destructive" onClick={remove} data-testid="member-remove-confirm-button">Remove</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
